/**
 * ProjectPanel — overview stats, external tools, launch phase, AI personas, feature status, tech stack.
 *
 * The "home base" panel for the admin dashboard. Shows live user/message counts,
 * links to external services, and project reference info.
 */
import { useState, useEffect } from 'react'
import { ExternalLink, UserPlus, Bookmark, CheckCircle2, LayoutDashboard } from 'lucide-react'
import { fetchDashboardStats, fetchRecentUsers } from '@/lib/queries/admin'
import { FREE_TIER_MESSAGE_LIMIT } from '@/lib/constants'
import CollapsibleSection, { InfoAccordion } from './CollapsibleSection'

// ── Static Data ─────────────────────────────────────────────────

const SECTIONS = [
  { key: 'hero', label: 'Hero Section', desc: 'Main headline, tagline, CTA', href: '/' },
  { key: 'features', label: 'Features', desc: 'Feature cards on landing page', href: '/' },
  {
    key: 'signup-cta',
    label: 'Signup CTA',
    desc: 'Landing-page free signup hero + newsletter form',
    href: '/#signup-cta',
  },
  {
    key: 'pricing',
    label: 'Pricing Page',
    desc: 'Plans, pricing, feature comparison',
    href: '/pricing',
  },
  {
    key: 'subscribe',
    label: 'Subscribe Page',
    desc: 'Premium subscription checkout',
    href: '/subscribe',
  },
  { key: 'chat', label: 'Chat Page', desc: 'Chat welcome message & header', href: '/chat' },
  { key: 'navbar', label: 'Navigation Bar', desc: 'Logo, nav links', href: '/' },
  { key: 'footer', label: 'Footer', desc: 'Logo, tagline, footer links', href: '/' },
  { key: 'privacy', label: 'Privacy Policy', desc: 'Privacy policy content', href: '/privacy' },
  { key: 'terms', label: 'Terms of Service', desc: 'Terms & conditions content', href: '/terms' },
]

const EXTERNAL_TOOLS = [
  {
    label: 'Supabase',
    href: 'https://supabase.com/dashboard/projects',
    badge: 'DB + Auth',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  {
    label: 'Vercel',
    href: 'https://vercel.com/dashboard',
    badge: 'Hosting',
    color: 'bg-gray-50 text-gray-700 border-gray-200',
  },
  {
    label: 'Stripe',
    href: 'https://dashboard.stripe.com',
    badge: 'Payments',
    color: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  {
    label: 'MailerLite',
    href: 'https://dashboard.mailerlite.com',
    badge: 'Email drip',
    color: 'bg-orange-50 text-orange-700 border-orange-200',
  },
  {
    label: 'Anthropic',
    href: 'https://console.anthropic.com',
    badge: 'AI API',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    label: 'GitHub',
    href: 'https://github.com/mchap2023-ship-it/Illuminate-AI',
    badge: 'Source',
    color: 'bg-gray-50 text-gray-900 border-gray-200',
  },
  {
    label: 'Google Cloud',
    href: 'https://console.cloud.google.com',
    badge: 'OAuth',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  {
    label: 'Resend',
    href: 'https://resend.com/dashboard',
    badge: 'SMTP',
    color: 'bg-red-50 text-red-700 border-red-200',
  },
  {
    label: 'Google Cloud',
    href: 'https://console.cloud.google.com',
    badge: 'DNS',
    color: 'bg-green-50 text-green-700 border-green-200',
  },
]

const LAUNCH_PHASES = [
  { label: 'Building', done: true },
  { label: 'Admin Testing', done: true },
  { label: 'Soft Launch', active: true },
  { label: 'Public Launch', done: false },
]

const AI_PERSONAS_INFO = [
  {
    name: 'Grace',
    tab: 'General',
    access: 'All users',
    color: '#4f86c6',
    desc: 'Warm faith companion — prayer, Scripture, spiritual growth, personal questions',
  },
  {
    name: 'Logos',
    tab: 'Apologetics',
    access: 'Premium',
    color: '#7c4dba',
    desc: 'Rigorous defender of the faith — arguments, evidence, philosophy, hard questions',
  },
  {
    name: 'Spurgeon',
    tab: 'Sermon Prep',
    access: 'Premium',
    color: '#c07d2e',
    desc: 'Sermon builder — outlines, illustrations, exegesis, application, style',
  },
  {
    name: 'Ezra',
    tab: 'Bible Study',
    access: 'Premium',
    color: '#2e8b57',
    desc: 'Deep Scripture study — context, original languages, commentary, study plans',
  },
]

const FEATURE_GROUPS = [
  {
    label: 'Live — All Users',
    badge: 'Public',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    features: [
      'Grace AI (General chatbot)',
      'Conversation history + sidebar',
      'Onboarding — 5-step personalization',
      'Chat preferences panel',
      `${FREE_TIER_MESSAGE_LIMIT} messages / month free tier`,
      'Daily faith prompts',
      'Prompt library',
      'Saved messages',
      'Follow-up question suggestions',
      'Like / dislike message feedback',
    ],
  },
  {
    label: 'Premium Only',
    badge: 'Gated',
    badgeColor: 'bg-blue-100 text-blue-700',
    features: [
      'Logos (Apologetics AI)',
      'Spurgeon (Sermon Prep AI)',
      'Ezra (Bible Study AI)',
      'Smart auto-routing to best AI tab',
      'Unlimited messages (4 096 tokens / response)',
      'Referral system + credits',
    ],
  },
  {
    label: 'Admin Only',
    badge: 'Internal',
    badgeColor: 'bg-gray-100 text-gray-700',
    features: [
      'Admin dashboard (this page)',
      'Invite code generator',
      'All Users management + drawer',
      'Finance panel (Stripe live data)',
      'Chat analytics',
      'Launch checklist',
      'Patch notes admin',
    ],
  },
]

const TECH_STACK_GROUPS = [
  {
    label: 'Frontend',
    color: 'bg-indigo-50 border-indigo-100',
    items: [
      { name: 'Vite + React 19', note: 'Build tool + UI framework' },
      { name: 'React Router DOM v7', note: 'Client-side routing' },
      { name: 'Tailwind CSS v4', note: '@theme block — no config file' },
    ],
  },
  {
    label: 'Backend & AI',
    color: 'bg-violet-50 border-violet-100',
    items: [
      { name: 'Supabase', note: 'DB + Auth + Edge Functions' },
      // Mirrors supabase/functions/_shared/config.ts CLAUDE_MODELS.HAIKU.
      // Display string only — the actual model ID lives in the edge functions.
      { name: 'Claude API', note: 'claude-haiku-4-5-20251001 · key in Vault' },
    ],
  },
  {
    label: 'Hosting & DevOps',
    color: 'bg-gray-50 border-gray-100',
    items: [
      { name: 'Vercel', note: 'Auto-deploys from GitHub main' },
      { name: 'GitHub', note: 'mchap2023-ship-it/Illuminate-AI' },
    ],
  },
  {
    label: 'Payments & Email',
    color: 'bg-emerald-50 border-emerald-100',
    items: [
      { name: 'Stripe', note: 'Subscriptions + payment links · 3 plans' },
      { name: 'Resend', note: 'SMTP — transactional from noreply@' },
      { name: 'MailerLite', note: '4 groups · newsletter signups + welcome automations' },
    ],
  },
  {
    label: 'Auth & Analytics',
    color: 'bg-amber-50 border-amber-100',
    items: [{ name: 'Google Cloud', note: 'OAuth — "Continue with Google"' }],
  },
]

// ── Component ───────────────────────────────────────────────────

export default function ProjectPanel() {
  const [stats, setStats] = useState({})
  const [recentSignups, setRecentSignups] = useState([])

  useEffect(() => {
    fetchDashboardStats()
      .then(setStats)
      .catch(() => {})
    fetchRecentUsers()
      .then(users => setRecentSignups((users || []).slice(0, 5)))
      .catch(() => {})
  }, [])

  return (
    <CollapsibleSection
      id="project"
      title="Project Overview"
      icon={LayoutDashboard}
      subtitle="Live stats + external tools"
      defaultOpen={true}
    >
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        {[
          { value: stats.totalUsers, label: 'Total users', color: 'text-indigo-600' },
          { value: stats.betaTesters, label: 'Invite-code users', color: 'text-emerald-600' },
          { value: stats.todayMessages, label: 'Msgs today', color: 'text-sky-600' },
          { value: stats.todaySignups, label: 'Signups today', color: 'text-violet-600' },
          { value: 20, label: 'Free msg limit', color: 'text-amber-600' },
          { value: 4, label: 'AI personas', color: 'text-blue-600' },
        ].map(({ value, label, color }) => (
          <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value ?? '—'}</p>
            <p className="text-xs text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* External tools + Recent Signups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /> External Tools
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {EXTERNAL_TOOLS.map(tool => (
              <a
                key={tool.label}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex flex-col gap-0.5 px-3 py-2.5 rounded-lg border text-xs font-medium transition-opacity hover:opacity-75 ${tool.color}`}
              >
                <span className="font-semibold">{tool.label}</span>
                <span className="opacity-60">{tool.badge}</span>
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <UserPlus className="w-3.5 h-3.5" aria-hidden="true" /> Recent Signups
          </p>
          {recentSignups.length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">No signups yet</p>
          ) : (
            <div className="space-y-2">
              {recentSignups.map(u => (
                <div key={u.id} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <span className="text-[10px] font-bold text-indigo-600">
                      {(u.display_name || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">
                      {u.display_name || 'Anonymous'}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {new Date(u.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  {u.is_premium && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium shrink-0">
                      Pro
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Project details */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Bookmark className="w-3.5 h-3.5" aria-hidden="true" /> Project Details
        </p>

        {/* Phase tracker */}
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-3">
          <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-3">Launch Phase</p>
          <div className="flex items-center gap-0">
            {LAUNCH_PHASES.map((phase, i) => (
              <div key={phase.label} className="flex items-center flex-1 min-w-0">
                <div className="flex flex-col items-center flex-1 min-w-0">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      phase.done
                        ? 'bg-emerald-500 text-white'
                        : phase.active
                          ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                          : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {phase.done ? '✓' : i + 1}
                  </div>
                  <span
                    className={`text-[10px] mt-1 text-center leading-tight ${phase.active ? 'text-indigo-600 font-semibold' : phase.done ? 'text-emerald-600' : 'text-gray-400'}`}
                  >
                    {phase.label}
                  </span>
                </div>
                {i < LAUNCH_PHASES.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-1 mb-4 ${phase.done ? 'bg-emerald-400' : 'bg-gray-200'}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quick facts */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {[
            {
              label: 'Live URL',
              value: 'illuminatefaithai.com',
              link: 'https://illuminatefaithai.com',
            },
            { label: 'AI Model', value: 'haiku-4-5-20251001', mono: true },
            { label: 'Free Tier', value: '20 msgs / month' },
            { label: 'Admin Email', value: 'hello@illuminatefaithai.com', mono: true },
          ].map(({ label, value, mono, link }) => (
            <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
              <dt className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</dt>
              <dd
                className={`text-xs text-gray-800 leading-snug mt-0.5 truncate ${mono ? 'font-mono' : ''}`}
              >
                {link ? (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {value}
                  </a>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
        </div>

        {/* Expandable sections */}
        <div className="space-y-2">
          <InfoAccordion
            label="AI Personas"
            badge="4 active"
            badgeColor="bg-blue-100 text-blue-700"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {AI_PERSONAS_INFO.map(p => (
                <div key={p.name} className="flex gap-3 items-start">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-gray-800">{p.name}</span>
                      <span className="text-[10px] text-gray-400">({p.tab})</span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p.access === 'All users' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}
                      >
                        {p.access}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </InfoAccordion>

          <InfoAccordion
            label="Feature Status"
            badge="by access level"
            badgeColor="bg-gray-100 text-gray-600"
          >
            <div className="space-y-4">
              {FEATURE_GROUPS.map(group => (
                <div key={group.label}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      {group.label}
                    </span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${group.badgeColor}`}
                    >
                      {group.badge}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {group.features.map(f => (
                      <div key={f} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <CheckCircle2
                          className="w-3 h-3 text-emerald-500 shrink-0"
                          aria-hidden="true"
                        />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </InfoAccordion>

          <InfoAccordion
            label="Tech Stack"
            badge="full breakdown"
            badgeColor="bg-gray-100 text-gray-600"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {TECH_STACK_GROUPS.map(group => (
                <div key={group.label} className={`border rounded-lg p-3 ${group.color}`}>
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {group.label}
                  </p>
                  <div className="space-y-1.5">
                    {group.items.map(item => (
                      <div key={item.name}>
                        <span className="text-xs font-semibold text-gray-800">{item.name}</span>
                        <span className="text-[10px] text-gray-400 ml-1">— {item.note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </InfoAccordion>
        </div>
      </div>
    </CollapsibleSection>
  )
}

export { SECTIONS }
